import { Add, ExpandMore } from '@mui/icons-material';
import { Box, Button, IconButton, List, MenuItem, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { Card, SMBSelect } from '../../../components';
import { getSecuritySensitiveRandomNumber } from '../../../helpers';
import { ICustomSubNavigationDetail } from '../../../models';
import { SortableNavGroup } from './SortableNavGroup';

// a little function to help us with reordering the result
const reorder = (list: any[], startIndex: number, endIndex: number): any[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  // update all of the items array sortOrder to be unique
  return result.map((r, i) => ({
    ...r,
    sortOrder: i + 1
  }));
};

interface INavigationSection {
  customNavigation: ICustomSubNavigationDetail[];
  setCustomNavigation: (val: ICustomSubNavigationDetail[]) => void;
  isEditing: {
    [id: string]: boolean;
  };
  setEditing: (val: { [id: number]: boolean }) => void;

  setSelectedNavType: (val: string) => void;
  selectedNavType: string;
}

export const NavigationSection: FC<INavigationSection> = ({
  isEditing,
  setEditing,
  customNavigation,
  setCustomNavigation,
  setSelectedNavType,
  selectedNavType
}) => {
  const [isCardExpanded, setCardExpanded] = useState<boolean>(false);
  const [isNavExpanded, setNavExpanded] = useState<{
    [item: string]: boolean;
  }>({});
  const [originalNavItems] = useState<ICustomSubNavigationDetail[]>(customNavigation);
  // made these consts since I had already named the vars this way, just a simple mapper at the moment;
  const navItems = customNavigation;
  const setNavItems = setCustomNavigation;

  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (result.type === 'droppableItem') {
      const items = reorder(navItems, sourceIndex, destIndex);

      setNavItems(items);
    } else if (result.type === 'droppableSubItem') {
      const itemSubItemMap = navItems.reduce((acc, item) => {
        acc[item.customNavigationId] = item.subNavigation;
        return acc;
      }, {});

      const sourceParentId = parseInt(result.source.droppableId);
      const destParentId = parseInt(result.destination.droppableId);

      const sourceSubItems = itemSubItemMap[sourceParentId];
      const destSubItems = itemSubItemMap[destParentId];

      let newItems = JSON.parse(JSON.stringify(navItems));

      /** In this case subItems are reOrdered inside same Parent */
      if (sourceParentId === destParentId) {
        const reorderedSubItems = reorder(sourceSubItems, sourceIndex, destIndex);
        newItems = newItems.map(item => {
          if (item.customNavigationId === sourceParentId) {
            item.subNavigation = reorderedSubItems;
          }
          return item;
        });
        setNavItems(newItems);
      } else {
        let newSourceSubItems = JSON.parse(JSON.stringify(sourceSubItems));
        const [draggedItem]: [item: ICustomSubNavigationDetail] = newSourceSubItems.splice(
          sourceIndex,
          1
        );
        const randomNumber = Math.floor(getSecuritySensitiveRandomNumber() * (100000 - 1 + 1) + 1);
        let newDestSubItems = JSON.parse(JSON.stringify(destSubItems));
        // when moving a sub item from one parent nav to another parent nav then update the customNavigationId
        // with a random number and se the isNew flag to true so upon save we set the customNavigationId to `null`
        // so the API knows to move it to a different parent
        newDestSubItems.splice(destIndex, 0, {
          ...draggedItem,
          customNavigationId: randomNumber,
          isNew: true
        });
        newItems = newItems
          .map(item => {
            if (item.customNavigationId === sourceParentId) {
              item.subNavigation = newSourceSubItems;
            } else if (item.customNavigationId === destParentId) {
              item.subNavigation = newDestSubItems;
            }
            return item;
          })
          .map((result, i) => ({
            ...result,
            sortOrder: i + 1,
            subNavigation: result.subNavigation.map((subNav, i) => ({
              ...subNav,
              sortOrder: i + 1
            }))
          }));
        setNavItems(newItems);
      }
    }
  };
  const classes = useStyles();
  return (
    <>
      <Box marginBottom="1rem">
        <SMBSelect
          variant="outlined"
          autoComplete="nope"
          label="Navigation Type"
          name="type"
          value={selectedNavType}
          size="small"
          onChange={e => {
            e.preventDefault();
            e.stopPropagation();
            if (e.target.value === 'None' && isCardExpanded) {
              setCardExpanded(false);
            }
            setSelectedNavType(e.target.value);
          }}
        >
          {['None', 'BuilderLinq', 'Custom'].map((type: string, index) => {
            return (
              <MenuItem key={`${index}`} value={type}>
                {type}
              </MenuItem>
            );
          })}
        </SMBSelect>
      </Box>
      <Card
        title="Manage Navigation"
        isExpanded={isCardExpanded}
        action={
          <IconButton
            disabled={selectedNavType === 'None'}
            onClick={() => setCardExpanded(!isCardExpanded)}
            aria-expanded={isCardExpanded}
            aria-label="show more"
          >
            <ExpandMore />
          </IconButton>
        }
        cardTitleClassName={classes.cardTitle}
        headerClassName={classes.header}
        cardWrapperClassName={classes.cardWrapper}
        cardHeaderActionClassName={classes.cardHeaderAction}
      >
        {selectedNavType === 'BuilderLinq' && (
          <>
            <Typography>Navigation will show the standard BuilderLinq options.</Typography>
          </>
        )}
        {selectedNavType === 'Custom' && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable" type="droppableItem">
              {(provided, snapshot) => (
                <div ref={provided.innerRef}>
                  <List>
                    {navItems.map((item, index) => {
                      return (
                        <SortableNavGroup
                          key={item.customNavigationId}
                          setEditing={setEditing}
                          isEditing={isEditing}
                          navItems={navItems}
                          index={index}
                          item={item}
                          setNavExpanded={setNavExpanded}
                          isNavExpanded={isNavExpanded}
                          setNavItems={setNavItems}
                          originalNavItems={originalNavItems}
                        />
                      );
                    })}
                  </List>
                  {!snapshot.isDraggingOver && (
                    <Button
                      color="secondary"
                      startIcon={<Add />}
                      sx={{ fontSize: '14px', padding: 0 }}
                      onClick={() => {
                        const copy = JSON.parse(JSON.stringify(navItems));
                        const randomNumber = Math.floor(getSecuritySensitiveRandomNumber() * (100000 - 1 + 1) + 1);
                        copy.push({
                          customNavigationId: randomNumber,
                          name: 'Nav Title',
                          url: 'url',
                          isNew: true,
                          isExternal: false,
                          sortOrder: navItems.length + 1,
                          subNavigation: []
                        });
                        setEditing({
                          [randomNumber]: true
                        });
                        setNavItems(copy);
                      }}
                    >
                      Add Nav Item
                    </Button>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Card>
    </>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  cardTitle: {
    textTransform: 'none',
    fontSize: '1.25rem'
  },
  cardTitleNew: {
    textTransform: 'none',
    fontSize: '1.25rem',
    fontStyle: 'italic'
  },
  cardWrapper: {
    flex: 1
  },
  header: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[200]}`
  },
  headerNew: {
    color: theme.palette.grey[400],
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[200]}`
  }
}));
